import React,{useState,useRef,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useDashboardStore from '../store/dashboardStore';
import {format,startOfWeek,endOfWeek,getWeek,getYear,parseISO,getDay,nextSunday,isSunday} from 'date-fns';

const {FiCalendar,FiX,FiFilter,FiChevronDown,FiTrendingUp,FiRepeat,FiBarChart3}=FiIcons;

const FilterStrip=()=> {
const {preset,dateFrom,dateTo,compare,departments,categories,products,setPreset,setDateRange,setCompare,setFilters}=useDashboardStore();

const presets=[
{key: 'DATE',label: 'Date',icon: FiTrendingUp,description: 'Single date with DoW vs DoW analysis'},
{key: 'WEEK',label: 'Week',icon: FiBarChart3,description: 'Current week with WoW analysis'},
{key: 'MTD',label: 'MTD'},
{key: 'YTD',label: 'YTD'},
{key: 'CUSTOM',label: 'Custom'}
];

// ENHANCED: Different comparisons available based on preset
const getAvailableComparisons=()=> {
const baseComparisons=[{key: 'NONE',label: 'No Comparison'}];

if (preset==='DATE') {
return [
...baseComparisons,
{key: 'DOW',label: 'DoW vs DoW',disabled: false}
];
} else if (preset==='WEEK') {
return [
...baseComparisons,
{key: 'WOW',label: 'WoW (12 weeks)',disabled: false}
];
} else if (preset==='CUSTOM') {
// For CUSTOM preset,return empty array to hide comparison selector completely
return [];
} else {
return [
...baseComparisons,
{key: 'DOW',label: 'DoW vs DoW',disabled: true},
{key: 'WOW',label: 'WoW',disabled: preset==='DATE'},
{key: 'MOM',label: 'MoM',disabled: preset==='DATE' || preset==='WEEK'}
];
}
};

const comparisons=getAvailableComparisons();

const activeFilters=[
...departments.map(d=> ({type: 'department',value: d})),
...categories.map(c=> ({type: 'category',value: c})),
...products.map(p=> ({type: 'product',value: p}))
];

const removeFilter=(filter)=> {
const newFilters={};
if (filter.type==='department') {
newFilters.departments=departments.filter(d=> d !==filter.value);
} else if (filter.type==='category') {
newFilters.categories=categories.filter(c=> c !==filter.value);
} else if (filter.type==='product') {
newFilters.products=products.filter(p=> p !==filter.value);
}
setFilters(newFilters);
};

// FIXED: Handle single date change for "Date" preset - preserve preset
const handleSingleDateChange=(newDate)=> {
console.log(`📅 Date preset: User selected date ${newDate}`);
// Use preservePreset=true to keep the DATE preset active
setDateRange(newDate,newDate,true);
};

// Handle date range changes for other presets
const handleDateFromChange=(newDate)=> {
setDateRange(newDate,dateTo);
};

const handleDateToChange=(newDate)=> {
setDateRange(dateFrom,newDate);
};

// Get current week info for display when WEEK preset is selected
const getCurrentWeekDisplay=()=> {
const currentDate=new Date();
const currentWeekNum=getWeek(currentDate,{weekStartsOn: 1});
const currentYear=getYear(currentDate);
const weekStart=startOfWeek(currentDate,{weekStartsOn: 1});
const weekEnd=endOfWeek(currentDate,{weekStartsOn: 1});
return `Week ${currentWeekNum} ${currentYear} (${format(weekStart,'MMM d')} - ${format(weekEnd,'MMM d')})`;
};

// Check if DATE preset is active and has automatic trend analysis
const hasAutoTrendAnalysis=preset==='DATE' && compare==='NONE';

// Check if DoW vs DoW is active
const isDowVsDowActive=compare==='DOW' && preset==='DATE';

// Check if WoW is active for WEEK preset
const isWowActive=compare==='WOW' && preset==='WEEK';

// Get DoW information for display
const getDowInfo=()=> {
if (!isDowVsDowActive) return null;
const selectedDate=new Date(dateFrom);
const dayOfWeek=getDay(selectedDate);
const dayNames=['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
return {
dayName: dayNames[dayOfWeek],
date: dateFrom
};
};

const dowInfo=getDowInfo();

// Handle comparison change - restrict based on preset
const handleComparisonChange=(newCompare)=> {
if (newCompare==='DOW' && preset !=='DATE') {
alert('DoW vs DoW comparison is only available with the DATE preset.');
return;
}

if (newCompare==='WOW' && preset !=='WEEK' && preset !=='MTD' && preset !=='YTD') {
alert('WoW comparison is available for WEEK,MTD,YTD presets.');
return;
}

if (preset==='DATE' && newCompare !=='NONE' && newCompare !=='DOW') {
alert('Only DoW vs DoW comparison is available with the DATE preset.');
return;
}

if (preset==='WEEK' && newCompare !=='NONE' && newCompare !=='WOW') {
alert('Only WoW comparison is available with the WEEK preset.');
return;
}

console.log(`📊 Comparison changed to: ${newCompare}`);
setCompare(newCompare);
};

// Handle preset change - clear incompatible comparisons and reset comparison for CUSTOM
const handlePresetChange=(newPreset)=> {
console.log(`📅 Preset changing from ${preset} to ${newPreset}`);

// Reset comparison when switching to CUSTOM preset
if (newPreset==='CUSTOM') {
console.log('📊 Switching to CUSTOM preset - resetting comparison to NONE');
setCompare('NONE');
}
// Reset comparison when switching presets to avoid conflicts
else if (newPreset==='DATE' && compare !=='NONE' && compare !=='DOW') {
console.log('📊 Switching to DATE preset - resetting comparison to NONE');
setCompare('NONE');
} else if (newPreset==='WEEK' && compare !=='NONE' && compare !=='WOW') {
console.log('📊 Switching to WEEK preset - resetting comparison to NONE');
setCompare('NONE');
} else if (preset==='DATE' && newPreset !=='DATE' && compare==='DOW') {
console.log('📊 Switching away from DATE preset - resetting DoW comparison to NONE');
setCompare('NONE');
} else if (preset==='WEEK' && newPreset !=='WEEK' && compare==='WOW') {
console.log('📊 Switching away from WEEK preset - resetting WoW comparison to NONE');
setCompare('NONE');
}

setPreset(newPreset);
};

return (
<motion.div
className="bg-brand-card border-b border-brand-muted2/20 px-6 py-4"
initial={{opacity: 0,y: -5}}
animate={{opacity: 1,y: 0}}
transition={{duration: 0.16,delay: 0.1}}
>
<div className="max-w-7xl mx-auto">
{/* Controls Row */}
<div className="flex flex-wrap items-center gap-4 mb-4">
{/* Preset Selector with integrated DATE field */}
<div className="flex items-center space-x-3">
{/* Preset buttons */}
<div className="flex bg-brand-muted2/10 rounded-button p-1">
{presets.map((p)=> (
<motion.button
key={p.key}
onClick={()=> handlePresetChange(p.key)}
className={`flex items-center space-x-2 px-3 py-1 rounded-lg text-sm transition-colors relative ${
preset===p.key
? 'bg-brand-accent text-brand-bg'
: 'text-brand-muted hover:text-brand-fg'
}`}
whileHover={{scale: 1.02}}
whileTap={{scale: 0.98}}
title={p.description || p.label}
>
{p.icon && preset===p.key && (
<SafeIcon icon={p.icon} className="w-3 h-3" />
)}
<span>{p.label}</span>
{((p.key==='DATE' && preset==='DATE') || (p.key==='WEEK' && preset==='WEEK')) && (
<div className="absolute -top-1 -right-1 w-2 h-2 bg-brand-good rounded-full animate-pulse" />
)}
</motion.button>
))}
</div>

{/* FIXED: Single date field appears RIGHT NEXT to Date preset when active */}
{preset==='DATE' && (
<motion.div
className="flex items-center space-x-2 bg-brand-muted2/10 rounded-button px-3 py-2"
initial={{opacity: 0,scale: 0.9}}
animate={{opacity: 1,scale: 1}}
transition={{duration: 0.2}}
>
<SafeIcon icon={FiCalendar} className="w-4 h-4 text-brand-accent" />
<input
type="date"
value={dateFrom}
onChange={(e)=> handleSingleDateChange(e.target.value)}
className="bg-transparent text-brand-fg text-sm border-none outline-none min-w-[130px]"
title="Select date for DoW vs DoW analysis"
/>
{hasAutoTrendAnalysis && (
<div className="flex items-center space-x-1 text-brand-good">
<SafeIcon icon={FiTrendingUp} className="w-3 h-3" />
<span className="text-xs">7d trend</span>
</div>
)}
{isDowVsDowActive && dowInfo && (
<div className="flex items-center space-x-1 text-brand-accent">
<SafeIcon icon={FiRepeat} className="w-3 h-3" />
<span className="text-xs">{dowInfo.dayName} vs 30</span>
</div>
)}
</motion.div>
)}
</div>

{/* Date Range for other presets (moved to right) */}
{preset==='WEEK' ? (
// Week display for WEEK preset with WoW indicator
<div className="flex items-center space-x-2 bg-brand-muted2/10 rounded-button px-3 py-2">
<SafeIcon icon={FiCalendar} className="w-4 h-4 text-brand-muted" />
<span className="text-sm text-brand-fg">
{getCurrentWeekDisplay()}
</span>
{isWowActive && (
<div className="flex items-center space-x-1 text-brand-accent">
<SafeIcon icon={FiBarChart3} className="w-3 h-3" />
<span className="text-xs">vs 12 weeks</span>
</div>
)}
</div>
) : preset !=='DATE' ? (
// Date range picker for MTD,YTD,CUSTOM presets
<div className="flex items-center space-x-2 bg-brand-muted2/10 rounded-button px-3 py-2">
<SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
<input
type="date"
value={dateFrom}
onChange={(e)=> handleDateFromChange(e.target.value)}
className="bg-transparent text-brand-fg text-sm border-none outline-none"
/>
<span className="text-brand-muted">to</span>
<input
type="date"
value={dateTo}
onChange={(e)=> handleDateToChange(e.target.value)}
className="bg-transparent text-brand-fg text-sm border-none outline-none"
/>
</div>
) : null}

{/* UPDATED: Comparison Selector - Hidden for CUSTOM preset */}
{comparisons.length > 0 && (
<div className="relative">
<select
value={compare}
onChange={(e)=> handleComparisonChange(e.target.value)}
className="bg-brand-muted2/10 text-brand-fg rounded-button px-3 py-2 text-sm border-none outline-none"
>
{comparisons.map((c)=> (
<option
key={c.key}
value={c.key}
className="bg-brand-card"
disabled={c.disabled}
>
{c.label}
{c.disabled && preset==='DATE' && c.key !=='DOW' ? ' (Not available for Date preset)' : ''}
{c.disabled && preset==='WEEK' && c.key !=='WOW' ? ' (Not available for Week preset)' : ''}
{c.disabled && preset !=='DATE' && c.key==='DOW' ? ' (Date preset required)' : ''}
{c.disabled && preset !=='WEEK' && c.key==='WOW' && preset !=='MTD' && preset !=='YTD' ? ' (Week+ presets only)' : ''}
</option>
))}
</select>
{(hasAutoTrendAnalysis || isDowVsDowActive || isWowActive) && (
<div className="absolute -top-1 -right-1 w-2 h-2 bg-brand-good rounded-full animate-pulse" />
)}
</div>
)}

{/* Filter Button */}
<motion.button
className="flex items-center space-x-2 bg-brand-muted2/10 hover:bg-brand-muted2/20 rounded-button px-3 py-2 text-sm text-brand-fg transition-colors"
whileHover={{scale: 1.02}}
whileTap={{scale: 0.98}}
>
<SafeIcon icon={FiFilter} className="w-4 h-4 text-brand-muted" />
<span>Filters</span>
</motion.button>
</div>

{/* Active Filters */}
{activeFilters.length > 0 && (
<motion.div
className="flex flex-wrap gap-2"
initial={{opacity: 0,height: 0}}
animate={{opacity: 1,height: 'auto'}}
transition={{duration: 0.2}}
>
{activeFilters.map((filter,index)=> (
<motion.div
key={`${filter.type}-${filter.value}`}
className="flex items-center space-x-2 bg-brand-accent/20 text-brand-accent rounded-chip px-3 py-1 text-sm"
initial={{opacity: 0,scale: 0.8}}
animate={{opacity: 1,scale: 1}}
transition={{duration: 0.12,delay: index * 0.05}}
>
<span className="capitalize">{filter.type}:</span>
<span>{filter.value}</span>
<motion.button
onClick={()=> removeFilter(filter)}
className="hover:bg-brand-accent/30 rounded-full p-0.5"
whileHover={{scale: 1.1}}
whileTap={{scale: 0.9}}
>
<SafeIcon icon={FiX} className="w-3 h-3" />
</motion.button>
</motion.div>
))}
</motion.div>
)}
</div>
</motion.div>
);
};

export default FilterStrip;